import { useEffect, useState } from "react";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { SettingsCard as Card } from "./SettingsCard";
import { readableRenameError } from "./OrgNamePrompt";

/**
 * KEY-743: "Your organisation" in Your settings — the permanent home of the rename the dial page's prompt offers
 * once, for whoever may edit the organisation (org.edit). Renames the organisation this session is signed in to,
 * through the same narrow route as the prompt: its name and nothing else.
 */
export function OrganisationNameCard() {
  const { me, refreshMe } = useAuth();
  const current = me?.organisationName ?? "";
  const [name, setName] = useState(current);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A rename made elsewhere (the prompt, another tab's refresh) arrives through /api/me; follow it.
  useEffect(() => setName(current), [current]);

  if (!me?.organisationId || !me.capabilities.includes("org.edit")) return null;

  const dirty = name.trim() !== current;

  async function save() {
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give your organisation a name before saving.");
      return;
    }
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.renameMyOrganisation(token, trimmed);
      await refreshMe();
      setSaved(true);
    } catch (e) {
      setError(readableRenameError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Your organisation" hint="The name everyone in your organisation sees, and the one on your plan.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 10 }}
      >
        <div>
          <label
            htmlFor="kd-organisation-name"
            style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5 }}
          >
            Organisation name
          </label>
          <input
            id="kd-organisation-name"
            value={name}
            maxLength={200}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
              if (error) setError(null);
            }}
            style={{
              font: "inherit",
              fontSize: 14,
              padding: "8px 11px",
              borderRadius: "var(--radius-sm)",
              border: "var(--border-w) solid var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
              width: "100%",
              maxWidth: 380,
            }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Never greyed out for a blank name (KEY-741): pressing it says what's missing. Only an unchanged name,
              like Your details above, has nothing to save. */}
          <button type="submit" className="kd-btn-primary kd-btn-sm" disabled={busy || (!dirty && name.trim() !== "")}>
            {busy ? "Saving…" : "Save"}
          </button>
          {saved && !dirty && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Saved.</span>}
        </div>
        {error && (
          <div role="alert" style={{ fontSize: 13, color: "var(--st-overdue)" }}>
            {error}
          </div>
        )}
      </form>
    </Card>
  );
}
